import React, { useEffect, useState } from 'react';
import { useAuth, useSignIn } from '@clerk/clerk-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.jsx';
import { Button } from '../../components/ui/button.jsx';
import { Input } from '../../components/ui/input.jsx';
import { Label } from '../../components/ui/label.jsx';
import { Separator } from '../../components/ui/separator.jsx';
import { Alert } from '../../components/ui/alert.jsx';
import { Loader2 } from 'lucide-react';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const { signIn, setActive } = useSignIn(); // Clerk authentication
    const { isSignedIn, isLoaded } = useAuth();

    const isAuthenticated = isSignedIn && isLoaded;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            const result = await signIn.create({
                identifier: email,
                password,
            });

            let redirectTo = '/';

            if (location.state?.from?.pathname && location.state?.from?.pathname !== '/login') {
                redirectTo = location.state?.from?.pathname;
            }
            
            if (result.status === 'complete') {
                await setActive({ session: result.createdSessionId });
                navigate(redirectTo, { replace: true }); // Redirect after successful login
            }
        } catch (err) {
            setError('Invalid email or password');
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/'); // Change to the route you want to redirect to
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    if (!isLoaded) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-background">
                <div className="flex flex-col items-center space-y-4">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-full min-w-full flex justify-center items-center">
            <Card className="w-full max-w-md p-6">
                <CardHeader>
                    <CardTitle>Login</CardTitle>
                </CardHeader>
                <CardContent>
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <span>{error}</span>
                        </Alert>
                    )}
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                required
                            />
                            <Link
                                to="/forgot-password"
                                className="text-sm text-blue-500 hover:text-blue-600 mt-2 inline-block"
                            >
                                Forgot password?
                            </Link>
                        </div>
                        <Separator className="my-4" />
                        <Button type="submit" className="w-full">Login</Button>
                    </form>
                    <p className='mt-2'>
                        <span className='mr-2'>Don't have an account?</span>
                        <Link className='text-blue-500 mr-2' to={"/signup"}>Signup</Link>
                    </p>
                </CardContent>
            </Card>
        </div>
    );
};

export default Login;
